import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'

import { Item, Props as ItemProps } from './Item'

export interface Props {
  cta?: ButtonProps
  claim?: string
  description?: string
  label?: string
  medias?: ItemProps[]
  title?: string
  variant?: Variant
}

export const Intro = memo(function Intro({
  claim,
  cta,
  description,
  label,
  medias,
  title,
  variant = 'default',
}: Props) {
  if (!claim) {
    return null
  }

  const { ref, inView } = useInView({
    triggerOnce: true,
  })

  return (
    <Container variant={variant}>
      <Head>
        {label ? <Label tag="h2" text={label} /> : null}

        <Claim text={claim} />
      </Head>

      {medias ? (
        <Items dial={8} ref={ref} row space="between">
          {medias.map((item, index) => (
            <Item
              key={index}
              index={index}
              isOdd={index % 2 ? true : false}
              isVisible={inView}
              {...item}
            />
          ))}
        </Items>
      ) : null}

      <Bottom>
        {title ? <Title tag="h3" text={title} /> : null}

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}

        {cta ? (
          <FadeInUp>
            <CTA variant="flat" {...cta} />
          </FadeInUp>
        ) : null}
      </Bottom>
    </Container>
  )
})

const Head = styled.div`
  max-width: 63rem;
  margin: auto;
  text-align: center;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const Claim = styled(AnimatedTitle)`
  ${styles.claim}

  color: ${({ theme }) => theme.colors.variants.neutralLight1};
`

const Items = styled(FlexBox)`
  margin-top: 8.125rem;
  position: relative;
  z-index: 1;

  > div,
  > video {
    aspect-ratio: 0.75;
    &.odd {
      width: 31.111vw;
      margin-bottom: 3.75rem;
    }
  }

  @media (max-width: 1199px) {
    margin-top: 3.75rem;

    > div,
    > video {
      display: none;
      &.odd {
        display: block;
        width: calc(100% - 2rem);
        margin: 0 auto;
      }
    }
  }
`

const Bottom = styled.div`
  max-width: 31rem;
  margin: 2rem auto 0;
  text-align: center;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 84px;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.label}

  @media (max-width: 1199px) {
    margin-bottom: 2rem;
  }
`

const Description = styled.div`
  ${styles.description}

  margin-top: 1.5rem;

  @media (max-width: 1199px) {
    margin-top: 0;
  }
`

const CTA = styled(Button)`
  margin-top: 2.25rem;

  @media (max-width: 1199px) {
    margin-top: 2rem;
  }
`

const Container = styled.section<ContainerProps>`
  padding: 9rem 1.5rem 0;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 65%;
    background: url('/background-dark-01.webp') no-repeat top center;
    background-size: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  ${({ theme, variant }) => {
    switch (variant) {
      case 'invert':
        return css`
          &:before {
            background-image: url('/background-dark-02.webp');
          }

          ${Title},
          ${Description} {
            color: ${theme.colors.variants.primaryDark};
          }
        `
    }
  }}

  @media (max-width: 1199px) {
    padding: 3.75rem 2rem 0;
    &:before {
      height: 60%;
    }
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'invert'
